import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";

import "./McpServerDetailsPage.scss";
import ChatbotLayout from "../../chatbot/shared/ChatbotLayout";
import Message from "../../../i18n/Message";
import {
  CheckIcon,
  ChevronLeftIcon,
  CopyIcon,
  GlobeIcon,
  LinkIcon,
  MessageSquareDashed,
  RefreshCcwIcon,
  Trash2Icon,
  UnplugIcon,
} from "lucide-react";

import {
  linkToMcpServerEmbed,
  linkToMcpServers,
  linkToMcpServerTab,
} from "../../../routes";
import {
  loadMcpPlaygrounds,
  deleteMcpPlayground,
} from "../store/mcpPlaygroundActionCreators";
import { mcpPlaygroundSelectors } from "../store/mcpPlaygroundReducer";
import PlaygroundWidget from "../playground-widget/PlaygroundWidget";
import LiveChatAnalytics from "./LiveChatAnalytics";
import Notify from "../../shared/Notify";
import McpServerSettingsTab from "./McpServerSettingsTab";
import Spinner from "../../../assets/icons/Spinner";
import ToggleSwitch from "../../shared/toggle/ToggleSwitch";
import { UIModes } from "../playground-embed/playgroundSettings";
import { useTheme } from "../../../shared/theme/ThemeContext";
import { DARK, LIGHT } from "../../../shared/theme/themeStore";
import useSubscriptionGate from "../../shared/subscription/useSubscriptionGate";
import UpgradeHint from "../../shared/subscription/UpgradeHint";
import useOauthResultToast from "../oauth/useOauthResultToast";
import PlaygroundAnalyzingView from "../servers/PlaygroundAnalyzingView";

const McpServerDetailsPage = () => {
  const { serverId, tabId } = useParams();
  const intl = useIntl();
  const nav = useNavigate();
  const dispatch = useDispatch();
  const { isDark, setTheme } = useTheme();
  const { isReadOnly, can, limit } = useSubscriptionGate();
  const playgrounds = useSelector(mcpPlaygroundSelectors.getPlaygrounds);
  const [tab, setTab] = useState(tabId || "preview");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [copyMcpUrlState, setCopyMcpUrlState] = useState(null);
  const [loadingMcpStatus, setLoadingMcpStatus] = useState(false);

  useEffect(() => {
    dispatch(loadMcpPlaygrounds(intl));
  }, [intl, dispatch]);

  // After the backend completes (or fails) the OAuth flow it redirects the browser back here with
  // ?oauth=success|error&reason=... The same hook is mounted on the server list, which is where a
  // failure lands when the flow state no longer identifies a server.
  useOauthResultToast();

  const playground = playgrounds?.find((item) => item.uid === serverId);

  // The details page does not otherwise auto-refresh. While the server is still being set up
  // (awaiting OAuth or analyzing) poll the list every 2s until it reaches a terminal status.
  // CREATED counts as analyzing: the plain create path fires analyzeMcp and leaves the row at
  // CREATED for the whole run, only ever writing ANALYZED or ERROR at the end.
  const pollStatus = playground?.status;
  useEffect(() => {
    if (
      pollStatus !== "PENDING_AUTH" &&
      pollStatus !== "ANALYZING" &&
      pollStatus !== "CREATED"
    )
      return;
    const id = setInterval(() => {
      dispatch(loadMcpPlaygrounds(intl));
    }, 2000);
    return () => clearInterval(id);
  }, [pollStatus, intl, dispatch]);

  // Keep the active tab in sync with the URL so /:serverId/:tabId deep-links work even when the
  // component stays mounted (React Router only swaps the param, it doesn't remount).
  useEffect(() => {
    if (tabId && tabId !== tab) setTab(tabId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabId]);

  // Selecting a tab reflects it in the URL (deep-linkable / back-button friendly).
  const goToTab = (t) => {
    setTab(t);
    nav(linkToMcpServerTab(serverId, t));
  };

  let statusIconName = "yellow";
  if (playground?.status === "ANALYZED" || playground?.status === "IN_USE") {
    statusIconName = "green";
  } else if (playground?.status === "ANALYZING") {
    statusIconName = "yellow";
  } else if (playground?.status === "ERROR") {
    statusIconName = "red";
  }

  const onDeleteConfirmed = () => {
    const onSuccess = () => {
      dispatch(loadMcpPlaygrounds(intl));
      setShowDeleteConfirm(false);
      Notify.success(<Message id="mcp_playground_deleted" />);
      nav(linkToMcpServers());
    };
    dispatch(
      deleteMcpPlayground(intl, playground.uid, onSuccess, () =>
        setShowDeleteConfirm(false),
      ),
    );
  };

  if (playgrounds && !playground) {
    return (
      <ChatbotLayout contentPanelClassName="mcp_server_details_page">
        <div className="title_row">
          <div className="title_column">
            <div className="breadcrumbs">
              <div onClick={() => nav(linkToMcpServers())}>
                <ChevronLeftIcon
                  size={16}
                  strokeWidth="2"
                  color="rgb(103, 116, 142)"
                />
                <Message id="back_to_mcp_playgrounds" />
              </div>
            </div>
          </div>
        </div>
        <div className="error_body">Server not found</div>
      </ChatbotLayout>
    );
  }

  if (playgrounds) {
    const isError = playground.status === "ERROR";
    // Preview / Live Chat Analytics are hidden for errored playgrounds, but Settings must
    // stay reachable so the owner can fix a bad URL. Force Settings active in that case.
    const activeTab = isError && tab !== "settings" ? "settings" : tab;
    // Until the analysis finishes there is nothing in any of the tabs: no tools, no description,
    // no examples, no live-chat seed. Show the wait instead of three empty tabs — and, in passing,
    // stop the settings form from autosaving a URL mid-analysis and starting a second one.
    // PENDING_AUTH is deliberately not included: that state is waiting for the owner to press
    // Connect, which lives in Settings.
    const isAnalyzing =
      playground.status === "ANALYZING" || playground.status === "CREATED";
    return (
      <ChatbotLayout contentPanelClassName="mcp_server_details_page">
        <div className="title_row">
          <div className="breadcrumbs">
            <div onClick={() => nav(linkToMcpServers())}>
              <ChevronLeftIcon
                size={16}
                strokeWidth="2"
                color="rgb(103, 116, 142)"
              />
              <Message id="back_to_mcp_playgrounds" />
            </div>
          </div>
          <div className="title_section">
            <div className="icon_container">
              <div className="icon_circle">
                <UnplugIcon size={24} color="#67748e" />
              </div>
            </div>
            <div className="title_column">
              <div className="title_value">
                {playground.name}
                <div className="actions_container">
                  <div className="confirm_container">
                    <button
                      className="btn no-btn btn-sm edit_title_button"
                      disabled={isReadOnly}
                      onClick={() => {
                        if (isReadOnly) return;
                        setShowDeleteConfirm(true);
                      }}
                    >
                      <Trash2Icon size={18} />
                      <Message id="delete" />
                    </button>
                    {showDeleteConfirm && (
                      <>
                        <div
                          className="cover"
                          onClick={() => setShowDeleteConfirm(false)}
                        ></div>
                        <div className="confirm_panel">
                          <button
                            className="btn bg-gradient-primary btn-sm"
                            onClick={onDeleteConfirmed}
                          >
                            <Message id="confirm_delete" />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                  <button
                    type="button"
                    className="btn no-btn btn-sm share_button"
                    disabled={isReadOnly}
                    onClick={() => {
                      if (isReadOnly) return;
                      const url = `${window.location.origin}/app/playground/${playground.uid}`;
                      navigator.clipboard.writeText(url).then(() => {
                        Notify.success(
                          <Message id="share_playground_link_success" />,
                        );
                      });
                    }}
                  >
                    <CopyIcon size={18} color="currentColor" />
                    <Message id="share_playground_link" />
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-dark btn-sm"
                    disabled={isReadOnly}
                    onClick={() => {
                      if (isReadOnly) return;
                      nav(linkToMcpServerEmbed(playground.uid));
                    }}
                  >
                    <MessageSquareDashed size={18} color="currentColor" />
                    <Message id="embed_playground" />
                  </button>
                  {isReadOnly && (
                    <UpgradeHint
                      messageId="upgrade_hint_readonly"
                      plan={undefined}
                    />
                  )}
                </div>
              </div>
              <div className="slug" title={playground.websiteUrl}>
                <GlobeIcon size={16} />
                <a
                  href={playground.websiteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {playground.websiteUrl}
                </a>
              </div>
              <div className="slug" title={playground.mcpServerUrl}>
                <LinkIcon size={16} />
                <a
                  href={playground.mcpServerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {playground.mcpServerUrl}
                </a>
                {/* Copy MCP Server URL icon */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    marginLeft: 4,
                    padding: 2,
                    borderRadius: 4,
                    transition: "background 0.2s",
                    userSelect: "none",
                  }}
                  title="Copy MCP Server URL"
                  onClick={async (e) => {
                    e.preventDefault();
                    if (copyMcpUrlState === playground.mcpServerUrl) return;
                    setCopyMcpUrlState(playground.mcpServerUrl);
                    try {
                      await navigator.clipboard.writeText(
                        playground.mcpServerUrl,
                      );
                    } catch (err) {}
                    setTimeout(() => setCopyMcpUrlState(null), 3000);
                  }}
                >
                  {copyMcpUrlState === playground.mcpServerUrl ? (
                    <CheckIcon
                      size={16}
                      color="#00c853"
                      style={{ marginLeft: 2 }}
                    />
                  ) : (
                    <CopyIcon
                      size={16}
                      color="#67748e"
                      style={{ marginLeft: 2 }}
                    />
                  )}
                </div>
              </div>
              <div className="slug">
                <div className={"status_icon " + statusIconName}></div>
                <Message id="mcp_server_status" />:
                <span className="status_badge">
                  {playground.status === "ANALYZED" && (
                    <Message id="mcp_server_status_analized" />
                  )}
                  {isAnalyzing && <Message id="mcp_server_status_analizing" />}
                  {playground.status === "PENDING_AUTH" && (
                    <Message id="mcp_server_status_pending_auth" />
                  )}
                  {playground.status === "ERROR" && (
                    <Message id="mcp_server_status_error" />
                  )}
                </span>
                {/* Copy MCP Server URL icon */}
                {isAnalyzing && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      cursor: loadingMcpStatus ? "default" : "pointer",
                      marginLeft: 4,
                      padding: 2,
                      borderRadius: 4,
                      transition: "background 0.2s",
                      userSelect: "none",
                      opacity: loadingMcpStatus ? 0.6 : 1,
                    }}
                    title="Reload status"
                    onClick={async (e) => {
                      e.preventDefault();
                      if (loadingMcpStatus) return;
                      setLoadingMcpStatus(true);
                      try {
                        await dispatch(loadMcpPlaygrounds(intl));
                      } finally {
                        setLoadingMcpStatus(false);
                      }
                    }}
                  >
                    {loadingMcpStatus ? (
                      <Spinner size={16} style={{ marginLeft: 2 }} />
                    ) : (
                      <RefreshCcwIcon
                        size={16}
                        color="#67748e"
                        style={{ marginLeft: 2 }}
                      />
                    )}
                  </div>
                )}
              </div>
              <div className="theme_toggle_row">
                <ToggleSwitch
                  value={isDark ? UIModes.DARK : UIModes.LIGHT}
                  onChange={(value) =>
                    setTheme(value === UIModes.DARK ? DARK : LIGHT)
                  }
                  optionLeft={{
                    label: "playground_ui_mode_light",
                    value: UIModes.LIGHT,
                  }}
                  optionRight={{
                    label: "playground_ui_mode_dark",
                    value: UIModes.DARK,
                  }}
                />
              </div>
            </div>
          </div>
        </div>
        {isAnalyzing ? (
          <PlaygroundAnalyzingView variant="page" />
        ) : (
          <>
            <div className="details_tabs">
              {!isError && (
                <div
                  className={
                    "details_tab " + (activeTab === "preview" ? "active" : "")
                  }
                  onClick={() => goToTab("preview")}
                >
                  <Message id="server_details_tab_preview" />
                </div>
              )}
              <div
                className={
                  "details_tab " + (activeTab === "settings" ? "active" : "")
                }
                onClick={() => goToTab("settings")}
              >
                <Message id="server_details_tab_settings" />
              </div>
              {!isError && (
                <div
                  className={
                    "details_tab " + (activeTab === "analytics" ? "active" : "")
                  }
                  onClick={() => goToTab("analytics")}
                >
                  <Message id="server_details_tab_analytics" />
                </div>
              )}
            </div>

            {activeTab === "settings" && (
              <McpServerSettingsTab playground={playground} />
            )}

            {/* LiveChatAnalytics self-gates: for a plan without Live Chat it renders its own
                lock/upgrade screen instead of the charts. */}
            {!isError && activeTab === "analytics" && (
              <LiveChatAnalytics playground={playground} />
            )}

            {!isError && activeTab === "preview" && (
              <div className="preview_container_wrapper">
                <div className="preview_container">
                  <PlaygroundWidget
                    playground={playground}
                    uiMode={isDark ? UIModes.DARK : UIModes.LIGHT}
                    manage={true}
                    liveChatAllowed={can("liveChat")}
                    toolLimit={limit("tools")}
                    readOnly={isReadOnly}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </ChatbotLayout>
    );
  } else {
    return null;
  }
};

export default McpServerDetailsPage;
